import React, { createContext, useContext, useState, useCallback } from 'react';

const CategoryContext = createContext();

export const useCategory = () => {
    const context = useContext(CategoryContext);
    if (!context) {
        throw new Error('useCategory must be used within a CategoryProvider');
    }
    return context;
};

export const CategoryProvider = ({ children }) => {
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [categoryPath, setCategoryPath] = useState([]);
    const [menuPath, setMenuPath] = useState([]);

    // For direct category selection from home.jsx
    const selectCategory = useCallback((category, path = []) => {
        console.group('CategoryContext: selectCategory');
        console.log('Direct Category Selection:', {
            category: category,
            categoryId: category?.id,
            path: path
        });

        setSelectedCategory(category);
        setCategoryPath(path);
        setMenuPath([]); // Clear menu path when directly selecting
        console.groupEnd();
    }, []);

    // For menu-based category selection
    const selectCategoryFromMenu = useCallback((menuItem, parentMenuItem = null) => {
        console.group('CategoryContext: selectCategoryFromMenu');
        console.log('Menu Item:', menuItem);
        console.log('Parent:', parentMenuItem);
        
        if (menuItem.category) {
            const categoryObject = {
                id: menuItem.category.id,
                title: menuItem.category.title || menuItem.name,
                parent_id: parentMenuItem?.category?.id || menuItem.category.parent_id || null,
            };

            let newPath = [];
            
            // If we have a parent menu item with a category, add it first
            if (parentMenuItem?.category) {
                newPath.push({
                    id: parentMenuItem.category.id,
                    title: parentMenuItem.category.title || parentMenuItem.name,
                    parent_id: null
                });
            }
            
            // Add the current category
            newPath.push(categoryObject);

            console.log('Setting category:', categoryObject);
            console.log('Setting path:', newPath);
            
            setSelectedCategory(categoryObject);
            setCategoryPath(newPath);
            setMenuPath(newPath);
        } else {
            // Handle "Shop All" or reset case
            console.log('Resetting category selection');
            setSelectedCategory(null);
            setCategoryPath([]);
            setMenuPath([]);
        }
        
        console.groupEnd();
    }, []);

    // Helper function to check if a category is currently selected
    const isCategorySelected = useCallback((categoryId) => {
        return selectedCategory?.id === categoryId || 
               categoryPath.some(cat => cat.id === categoryId);
    }, [selectedCategory, categoryPath]);

    // Helper function to get the full path to a category
    const getCategoryPath = useCallback((categoryId) => {
        if (menuPath.some(cat => cat.id === categoryId)) {
            return menuPath;
        }
        return categoryPath;
    }, [menuPath, categoryPath]);

    return (
        <CategoryContext.Provider value={{ 
            selectedCategory,
            categoryPath,
            menuPath,
            selectCategory,
            selectCategoryFromMenu,
            isCategorySelected,
            getCategoryPath
        }}>
            {children}
        </CategoryContext.Provider>
    );
};
